<script setup lang="ts">
import StarterKit from "@tiptap/starter-kit";
import { NodeSelection, TextSelection } from "prosemirror-state";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/vue-3";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import ButtonEditor from "~/components/Common/Rich-text-editor/ButtonEditor.vue";
import ButtonGroupEditor from "~/components/Common/Rich-text-editor/ButtonGroupEditor.vue";
import DropdownEditor from "~/components/Common/Rich-text-editor/DropdownEditor.vue";
import ColorDropdownEditor from "~/components/Common/Rich-text-editor/ColorDropdownEditor.vue";
import LinkModal from "~/components/Common/Rich-text-editor/LinkModal.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import ButtonImageEditor from "./ButtonImageEditor.vue";
import VariableDropdownEditor from "./VariableDropdownEditor.vue";
import { ResizableImage } from "./ResizableImage";
import { VariableBlock } from "./VariableBlock";
import type { EditorVariable } from "~/utils/options/editor.options";
import { useUploadDocumentStore } from "~/store/upload-document/upload-document.store";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import HardBreak from "@tiptap/extension-hard-break";
import BulletList from "@tiptap/extension-bullet-list";

const uploadDocumentStore = useUploadDocumentStore();
const emit = defineEmits(["update:modelValue", "update:deletedImgList"]);
const props = defineProps({
  modelValue: {
    type: String,
  },
  actionsList: {
    type: Array as PropType<string[]>,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  deletedImgList: {
    type: Array as PropType<string[]>,
  },
  placeholder: {
    type: String,
    default: "Commencez à écrire ici...",
    required: false,
  },
  variables: {
    type: Array as PropType<EditorVariable[]>,
    default: () => [],
  },
});
// Flag to prevent infinite loops during content updates
const emitAfterOnUpdate = ref(false);

// Map to track base64 URLs and their corresponding final uploaded URLs
// This prevents base64 URLs from being added to deletedImgList
const imageUrlMap = ref<Map<string, string>>(new Map());

/**
 * Extract all image URLs from the TipTap document
 * @param doc - ProseMirror document node
 * @returns Array of image source URLs
 */
function getAllImages(doc: ProseMirrorNode): string[] {
  const images: string[] = [];
  doc.descendants((node) => {
    if (node.type.name === "image") {
      const src = node.attrs.src;
      images.push(src);
    }
  });
  return images;
}

// Track images that have been deleted from the editor
// Only contains final uploaded URLs, never base64 URLs
const deletedImgList = ref<string[]>(props.deletedImgList || []);

/**
 * Extension to block image paste/drop when image functionality is disabled
 * This ensures no images can be added to the editor when 'image' is not in actionsList
 */
const ImageBlocker = Extension.create({
  name: "imageBlocker",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("imageBlocker"),
        props: {
          /**
           * Block image paste from clipboard
           */
          handlePaste(view, event, slice) {
            const items = Array.from(event.clipboardData?.items || []);
            for (const item of items) {
              if (item.type.indexOf("image") === 0) {
                event.preventDefault();
                return true; // Prevent default paste behavior
              }
            }
            return false; // Allow other paste operations
          },

          /**
           * Block image drag & drop
           */
          handleDrop(view, event, slice, moved) {
            if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
              const hasImages = Array.from(event.dataTransfer.files).some((file) => file.type.startsWith("image/"));

              if (hasImages) {
                event.preventDefault();
                return true; // Prevent default drop behavior
              }
            }
            return false; // Allow other drop operations
          },
        },
      }),
    ];
  },
});

const editor = useEditor({
  content: props.modelValue,

  /**
   * Called whenever the editor content is updated
   * Handles image tracking and deletion logic
   */
  onUpdate: async (ed) => {
    // Prevent infinite update loops
    emitAfterOnUpdate.value = true;

    // Emit the updated HTML content to parent component
    emit("update:modelValue", editor.value?.getHTML());

    // Only track images if image functionality is enabled
    if (props.actionsList?.find((i) => i === "image")) {
      // Get current images in the document
      const currentImages = getAllImages(ed.editor.state.doc);
      // Keep a copy of previous images for comparison
      const previousImages = [...imagesList.value];

      // Find images that were removed (present in previous but not in current)
      const deletedImages = previousImages.filter((src) => !currentImages.includes(src));

      if (deletedImages.length > 0) {
        deletedImages.forEach((img) => {
          // Only add real URLs to deletedImgList, never base64 URLs
          // Base64 URLs are temporary and don't exist on the server
          if (!img.startsWith("data:image/") && !deletedImgList.value.includes(img)) {
            deletedImgList.value.push(img);
          }

          // Clean up the URL mapping if a final URL was deleted
          // This prevents memory leaks and maintains mapping consistency
          for (const [base64Url, finalUrl] of imageUrlMap.value.entries()) {
            if (finalUrl === img) {
              imageUrlMap.value.delete(base64Url);
              break;
            }
          }
        });

        // Notify parent component about deleted images
        emit("update:deletedImgList", deletedImgList.value);
      }

      // Update the current images list for next comparison
      imagesList.value = currentImages;
    }
  },
  /**
   * Called before the editor is created
   * Sets up dependency injection for extensions
   */
  onBeforeCreate: ({ editor }) => {
    // Inject upload store so ResizableImage extension can access it
    editor.storage.uploadDocumentStore = uploadDocumentStore;

    /**
     * Inject URL mapping function for ResizableImage extension
     * This allows the extension to register base64 -> final URL mappings
     * and update our local imagesList to prevent false "deleted" notifications
     */
    editor.storage.mapImageUrl = (base64Url: string, finalUrl: string) => {
      // Store the mapping for later reference
      imageUrlMap.value.set(base64Url, finalUrl);

      // Update imagesList to replace base64 URL with final URL
      // This prevents the base64 URL from being considered "deleted"
      const index = imagesList.value.indexOf(base64Url);
      if (index !== -1) {
        imagesList.value[index] = finalUrl;
      }
    };
  },
  extensions: [
    // Core TipTap extensions - disable default HardBreak
    StarterKit.configure({
      hardBreak: false, // Disable HardBreak from StarterKit
    }),
    // Configure HardBreak to respond to Enter key
    HardBreak.extend({
      addKeyboardShortcuts() {
        return {
          Enter: () => {
            if (this.editor.isActive("listItem")) {
              return this.editor.commands.splitListItem("listItem");
            }
            return this.editor.commands.setHardBreak();
          },
        };
      },
    }),
    Underline,
    Link.configure({ openOnClick: false }),
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    Color.configure({
      types: ["textStyle", "blockquote"],
    }),
    TextStyle,
    Placeholder.configure({
      placeholder: props.placeholder,
    }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
    BulletList.configure({
      keepMarks: true,
      keepAttributes: true,
    }),
    // Conditional image handling based on actionsList
    // If 'image' is in actionsList: add ResizableImage extension with copy-paste enabled
    ...(props.actionsList?.includes("image")
      ? [
          ResizableImage.configure({
            allowBase64: true, // Allow base64 images for immediate feedback
            inline: true, // Allow inline images
            enableImagePaste: true, // Enable copy-paste and drag-drop functionality
          }),
        ]
      : []),
    // If 'image' is NOT in actionsList: add ImageBlocker to prevent any image operations
    ...(!props.actionsList?.includes("image") ? [ImageBlocker] : []),
    // Conditional variable block based on actionsList
    ...(props.actionsList?.includes("variable") ? [VariableBlock] : []),
  ],
  editorProps: {
    attributes: {
      spellcheck: "false",
      class: "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl m-5 focus:outline-none",
    },
  },
  editable: !props.disabled,
});

onMounted(() => {
  if (props.disabled) {
    editor.value?.setOptions({
      editorProps: {
        attributes: {
          class: "disabled text-secondary-color border-transparent",
        },
      },
    });
  }
});
const imagesList = ref<string[]>([]);

watch(
  () => editor.value?.state.doc,
  (newDoc) => {
    if (newDoc && !emitAfterOnUpdate.value) {
      imagesList.value = getAllImages(newDoc);
    }
  },
  { immediate: true },
);

watch(
  () => props.modelValue,
  async (newVal) => {
    if (emitAfterOnUpdate.value) {
      emitAfterOnUpdate.value = false;
      return;
    }
    editor.value?.commands.setContent(newVal || "");
  },
  { immediate: true },
);

watch(
  () => props.deletedImgList,
  (newVal) => {
    if (newVal) {
      deletedImgList.value = [...newVal];
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  editor.value?.destroy();
  // Clean up image mappings
  imageUrlMap.value.clear();
});

const setHeading = (level: any) => editor.value?.chain().focus().toggleHeading({ level }).run();
const addLink = () => {
  const url = prompt("Enter the URL");
  if (url) {
    editor.value?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }
};
const setTextColor = (color: string) => {
  editor.value?.chain().focus().setColor(color).run();
};
const highlightTextColor = (color: string) => {
  editor.value?.chain().focus().toggleHighlight({ color: color }).run();
};

const colorsActions = [
  {
    name: "#15181F",
    label: "Default-Black",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#15181F" }),
    default: true,
  },
  {
    name: "#505968",
    label: "Gray",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#505968" }),
  },
  {
    name: "#FEA62C",
    label: "Orange",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#FEA62C" }),
  },
  {
    name: "#FFCE55",
    label: "Yellow",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#FFCE55" }),
  },
  {
    name: "#1EE299",
    label: "Green",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#1EE299" }),
  },
  {
    name: "#0B92FC",
    label: "Blue",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#0B92FC" }),
  },
  {
    name: "#BF98FD",
    label: "Purple",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#BF98FD" }),
  },
  {
    name: "#FF6760",
    label: "Red",
    icon: "font-color",
    function: (color: string) => setTextColor(color),
    isActive: () => editor.value?.isActive("textStyle", { color: "#FF6760" }),
  },
];
const highlightColorsActions = computed(() => [
  {
    name: "#505968",
    label: "default",
    icon: "font-default-highlight",
    function: () => editor.value?.chain().focus().unsetHighlight().run(),
    isActive: () => editor.value?.isActive("highlight", { color: "#505968" }),
    default: true,
  },
  {
    name: "#15181F",
    label: "black",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#15181F" }),
  },
  {
    name: "#505968",
    label: "Gray",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#505968" }),
  },
  {
    name: "#FEA62C",
    label: "Orange",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#FEA62C" }),
  },
  {
    name: "#FFCE55",
    label: "Yellow",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#FFCE55" }),
  },
  {
    name: "#1EE299",
    label: "Green",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#1EE299" }),
  },
  {
    name: "#0B92FC",
    label: "Blue",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#0B92FC" }),
  },
  {
    name: "#BF98FD",
    label: "Purple",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#BF98FD" }),
  },
  {
    name: "#FF6760",
    label: "Red",
    icon: "font-highlight",
    function: (color: string) => highlightTextColor(color),
    isActive: () => editor.value?.isActive("highlight", { color: "#FF6760" }),
  },
]);
const groupActionsButtons = [
  {
    name: "bold",
    label: "Bold",
    icon: "bold",
    function: () => editor.value?.chain().focus().toggleBold().run(),
    visible: !!props.actionsList?.find((i) => i === "default"),
  },
  {
    name: "italic",
    label: "Italic",
    icon: "italic",
    function: () => editor.value?.chain().focus().toggleItalic().run(),
    visible: !!props.actionsList?.find((i) => i === "default"),
  },
  {
    name: "underline",
    label: "Underline",
    icon: "underline",
    function: () => editor.value?.chain().focus().toggleUnderline().run(),
    visible: !!props.actionsList?.find((i) => i === "default"),
  },
  {
    name: "strike",
    label: "Strikethrough",
    icon: "strike",
    function: () => editor.value?.chain().focus().toggleStrike().run(),
    visible: !!props.actionsList?.find((i) => i === "default"),
  },
];

const actionsButtons = [
  {
    name: "image",
    label: "Image",
    icon: "upload",
    function: async (image: string) => {
      if (image) {
        if (image.startsWith("data:")) {
          const [metadata, base64Data] = image.split(",");
          const mimeType = metadata.match(/:(.*?);/)?.[1] || "image/png";
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: mimeType });
          const fileName = `image_${new Date().getTime()}.${mimeType.split("/")[1]}`;
          const file = new File([blob], fileName, { type: mimeType });

          const uploadedFile = await uploadDocumentStore.uploadFileToS3fromTemplateEditor(file);
          if (uploadedFile) {
            editor.value?.chain().focus().setImage({ src: uploadedFile }).run();

            imagesList.value.push(uploadedFile);
          }
        }
      }
    },
    visible: !!props.actionsList?.find((i) => i === "image"),
  },
  {
    name: "link",
    label: "Link",
    icon: "link-editor",
    function: () => openModal(),
    visible: !!props.actionsList?.find((i) => i === "link"),
  },
  {
    name: "blockquote",
    label: "Quote",
    icon: "blockquote",
    function: () => editor.value?.chain().focus().toggleBlockquote().run(),
    visible: !!props.actionsList?.find((i) => i === "blockquote"),
  },
  {
    name: "separator",
    label: "Separator",
    icon: "separator",
    function: () => editor.value?.chain().focus().setHorizontalRule().run(),
    visible: !!props.actionsList?.find((i) => i === "separator"),
  },
  {
    name: "bulletList",
    label: "Bullet list",
    icon: "material-symbols-light:format-bold",
    function: () => editor.value?.chain().focus().toggleBulletList().run(),
    visible: !!props.actionsList?.find((i) => i === "bulletList"),
  },
  {
    name: "orderedList",
    label: "Numbered list",
    icon: "material-symbols-light:format-bold",
    function: () => editor.value?.chain().focus().toggleOrderedList().run(),
    visible: !!props.actionsList?.find((i) => i === "orderedList"),
  },
  {
    name: "table",
    label: "Table",
    icon: "table",
    function: () => editor.value?.chain().focus().insertTable({ rows: 3, cols: 3 }).run(),
    visible: !!props.actionsList?.find((i) => i === "table"),
  },
];
const justifyActions = [
  {
    name: "justify",
    label: "Justify",
    icon: "align-justify",
    function: () => editor.value?.chain().focus().setTextAlign("justify").run(),
    isActive: () => editor.value?.isActive({ textAlign: "justify" }),
  },
  {
    name: "left",
    label: "Left",
    icon: "align-left",
    function: () => editor.value?.chain().focus().setTextAlign("left").run(),
    isActive: () => editor.value?.isActive({ textAlign: "left" }),
    default: true,
  },
  {
    name: "center",
    label: "center",
    icon: "align-center",
    function: () => editor.value?.chain().focus().setTextAlign("center").run(),
    isActive: () => editor.value?.isActive({ textAlign: "center" }),
  },
  {
    name: "right",
    label: "Right",
    icon: "align-right",
    function: () => editor.value?.chain().focus().setTextAlign("right").run(),
    isActive: () => editor.value?.isActive({ textAlign: "right" }),
  },
];
const toggleListCurrentLine = (listType: "bulletList" | "orderedList") => {
  if (!editor.value) return;

  const toggleCommand = listType === "bulletList" ? "toggleBulletList" : "toggleOrderedList";

  if (editor.value.isActive(listType)) {
    editor.value.chain().focus()[toggleCommand]().run();
    return;
  }

  const { state, view } = editor.value;
  const { $from } = state.selection;
  const parent = $from.parent;

  if (parent.type.name !== "paragraph") {
    editor.value.chain().focus()[toggleCommand]().run();
    return;
  }

  let hasHardBreak = false;
  parent.forEach((node) => {
    if (node.type.name === "hardBreak") hasHardBreak = true;
  });

  if (!hasHardBreak) {
    editor.value.chain().focus()[toggleCommand]().run();
    return;
  }

  const schema = state.schema;
  const cursorOffset = $from.parentOffset;
  const parentPos = $from.before($from.depth);
  const parentEndPos = $from.after($from.depth);

  const lines: { from: number; to: number }[] = [];
  let lineStart = 0;

  parent.forEach((node, offset) => {
    if (node.type.name === "hardBreak") {
      lines.push({ from: lineStart, to: offset });
      lineStart = offset + node.nodeSize;
    }
  });
  lines.push({ from: lineStart, to: parent.content.size });

  let cursorLineIndex = lines.length - 1;
  for (let i = 0; i < lines.length; i++) {
    if (cursorOffset >= lines[i].from && cursorOffset <= lines[i].to) {
      cursorLineIndex = i;
      break;
    }
  }

  const paragraphs = lines.map((line) => {
    const content = parent.content.cut(line.from, line.to);
    return schema.nodes.paragraph.create(parent.attrs, content);
  });

  const { tr } = state;
  tr.replaceWith(parentPos, parentEndPos, paragraphs);

  let targetPos = parentPos;
  for (let i = 0; i < cursorLineIndex; i++) {
    targetPos += paragraphs[i].nodeSize;
  }
  targetPos += 1;

  tr.setSelection(TextSelection.create(tr.doc, targetPos));
  view.dispatch(tr);

  nextTick(() => {
    editor.value?.chain().focus()[toggleCommand]().run();
  });
};

const listActions = [
  {
    name: "bulletList",
    label: "Bullet list",
    icon: "bullet-list",
    function: () => toggleListCurrentLine("bulletList"),
  },
  {
    name: "orderedList",
    label: "Numbered list",
    icon: "ordered-list",
    function: () => toggleListCurrentLine("orderedList"),
  },
];

const listUndoRedo = [
  {
    name: "undo",
    label: "Undo",
    icon: "undo",
    function: () => editor.value?.commands.undo(),
  },
  {
    name: "redo",
    label: "Redo",
    icon: "redo",
    function: () => editor.value?.commands.redo(),
  },
];
const textStyleActions = [
  {
    name: "heading",
    label: "Heading 1",
    icon: "heading-01",
    function: () => setHeading(1),
    level: 1,
    isActive: () => editor.value?.isActive("heading", { level: 1 }),
  },
  {
    name: "heading",
    label: "Heading 2",
    icon: "heading-02",
    function: () => setHeading(2),
    level: 2,
    isActive: () => editor.value?.isActive("heading", { level: 2 }),
  },
  {
    name: "heading",
    label: "Heading 3",
    icon: "heading-03",
    function: () => setHeading(3),
    level: 3,
    isActive: () => editor.value?.isActive("heading", { level: 3 }),
  },
  {
    name: "paragraph",
    label: "Paragraph",
    icon: "paragraph",
    function: () => editor.value?.chain().focus().setParagraph().run(),
    default: true,
    isActive: () => editor.value?.isActive("paragraph"),
  },
];

const tableActions = [
  {
    name: "addColumnBefore",
    label: "Add column before",
    icon: "add-column-before",
    function: () => editor.value?.chain().focus().addColumnBefore().run(),
  },
  {
    name: "addColumnAfter",
    label: "Add column after",
    icon: "add-column-after",
    function: () => editor.value?.chain().focus().addColumnAfter().run(),
  },
  {
    name: "addRowBefore",
    label: "add row before",
    icon: "add-row-before",
    function: () => editor.value?.chain().focus().addRowBefore().run(),
  },
  {
    name: "addRowAfter",
    label: "add row after",
    icon: "add-row-after",
    function: () => editor.value?.chain().focus().addRowAfter().run(),
  },
];

// LINK
const modalLinkIsOpen = ref(false);
const url = ref("");

const openModal = () => {
  url.value = editor.value?.getAttributes("link").href;
  modalLinkIsOpen.value = true;
};

const closeModal = () => {
  modalLinkIsOpen.value = false;
  url.value = "";
};

const saveLink = () => {
  if (url.value) {
    editor.value?.chain().focus().extendMarkRange("link").setLink({ href: url.value, target: "_blank" }).run();
  } else {
    editor.value?.chain().focus().extendMarkRange("link").unsetLink().run();
  }
  editor.value?.commands.blur();
  closeModal();
};

const setAlign = (align: string) => {
  editor.value?.chain().focus().setTextAlign(align).run();
  editor.value?.commands.blur();
};
const removeLink = () => {
  editor.value?.chain().focus().extendMarkRange("link").unsetLink().run();
  closeModal();
};

const setSelectedNodeAlign = (align: string) => {
  if (!editor.value?.state) return;
  const { selection } = editor.value.state;
  if (!(selection instanceof NodeSelection)) {
    setAlign(align);
    return;
  }
  if (selection.node?.type?.name !== "variableBlock") {
    setAlign(align);
    return;
  }
  const newAlign = selection.node.attrs.align === align ? "none" : align;
  editor.value.chain().focus().updateAttributes("variableBlock", { align: newAlign }).run();
};
</script>

<template>
  <div v-if="editor" class="editor-container w-full">
    <div v-if="!props.disabled" class="control-group">
      <ButtonGroupEditor
        v-if="props.actionsList?.includes('default')"
        :editor="editor"
        :actions="groupActionsButtons"
      />
      <div class="flex gap-1 items-center">
        <div v-if="props.actionsList?.includes('color-text')" class="color-group flex">
          <ColorDropdownEditor
            id="textStyle"
            :position-left="-40"
            :position-top="-85"
            :actions="colorsActions"
            double="first"
            :editor="editor"
            type="textStyle"
          />
          <ColorDropdownEditor
            id="highlight"
            :position-left="-40"
            :position-top="-85"
            :actions="highlightColorsActions"
            double="last"
            :editor="editor"
            type="highlight"
          />
        </div>
        <DropdownEditor
          v-if="props.actionsList?.includes('text-style')"
          id="text-style"
          :position-left="-40"
          :position-top="-85"
          :actions="textStyleActions"
          :editor="editor"
        />
        <DropdownEditor
          v-if="props.actionsList?.includes('justify')"
          id="justify"
          :position-left="-40"
          :position-top="-85"
          :actions="justifyActions"
          :editor="editor"
          type="textAlign"
        />
        <ButtonGroupEditor
          v-if="!!props.actionsList?.find((i) => i === 'list')"
          :editor="editor"
          :actions="listActions"
        />
      </div>
      <div
        v-if="
          props.actionsList?.includes('link') ||
          props.actionsList?.includes('blockquote') ||
          props.actionsList?.includes('separator') ||
          props.actionsList?.includes('variable')
        "
        class="flex gap-1"
      >
        <ButtonImageEditor
          v-if="props.actionsList?.includes('image')"
          :editor="editor"
          :action="actionsButtons.find((i) => i.name === 'image')"
        />
        <ButtonEditor
          v-if="props.actionsList?.includes('link')"
          :editor="editor"
          :action="actionsButtons.find((i) => i.name === 'link')"
        />
        <ButtonEditor
          v-if="props.actionsList?.includes('blockquote')"
          :editor="editor"
          :action="actionsButtons.find((i) => i.name === 'blockquote')"
        />
        <ButtonEditor
          v-if="props.actionsList?.includes('table')"
          :editor="editor"
          :action="actionsButtons.find((i) => i.name === 'table')"
        />
        <ButtonEditor
          v-if="props.actionsList?.includes('separator')"
          :editor="editor"
          :action="actionsButtons.find((i) => i.name === 'separator')"
        />
        <VariableDropdownEditor
          v-if="props.actionsList?.includes('variable')"
          :editor="editor"
          :variables="props.variables"
        />
      </div>
      <ButtonGroupEditor :editor="editor" :actions="listUndoRedo" />
    </div>
    <BubbleMenu
      v-if="editor"
      plugin-key="bubbleMenuLink"
      :should-show="
        ({ editor, from, to }: any) => {
          return from === to && editor.isActive('link');
        }
      "
      :tippy-options="{ duration: 150, zIndex: 500 }"
      :editor="editor"
    >
      <div class="container-bubble">
        <button class="bubble-button p-2 rounded-lg" @click="openModal">
          <SvgIcon style="width: 16px; height: 16px" class="text-secondary-color" name="edit"></SvgIcon>
        </button>
        <button class="bubble-button p-2 rounded-lg" @click="removeLink">
          <SvgIcon style="width: 16px; height: 16px" class="text-secondary-color" name="delete_trash"></SvgIcon>
        </button>
      </div>
    </BubbleMenu>
    <BubbleMenu
      v-if="editor"
      plugin-key="bubbleMenuTable"
      :should-show="
        ({ editor, from, to }: any) => {
          return (
            from === to &&
            (editor.isActive('table') ||
              editor.isActive('tableCell') ||
              editor.isActive('tableHeader') ||
              editor.isActive('tableRow'))
          );
        }
      "
      :tippy-options="{ duration: 150, zIndex: 500 }"
      :editor="editor"
    >
      <div class="container-bubble">
        <button
          v-for="action in tableActions"
          :key="action.name"
          class="bubble-button p-2 rounded-lg"
          @click="action.function()"
        >
          <SvgIcon style="width: 16px; height: 16px" class="text-secondary-color" :name="action.icon"> </SvgIcon>
        </button>
      </div>
    </BubbleMenu>

    <BubbleMenu
      v-if="editor"
      plugin-key="bubbleMenuAlign"
      :should-show="
        () => {
          if (!editor?.state) return false;
          const selection = editor.state.selection;
          if (!(selection instanceof NodeSelection)) return false;
          const nodeName = selection.node?.type?.name;
          if (nodeName === 'image') return true;
          if (nodeName === 'variableBlock') return !!props.actionsList?.includes('variable');
          return false;
        }
      "
      :tippy-options="{ duration: 150, zIndex: 500 }"
      :editor="editor"
    >
      <div class="container-bubble">
        <button
          v-for="align in ['left', 'center', 'right']"
          :key="align"
          class="bubble-button p-2 rounded-lg"
          @click="setSelectedNodeAlign(align)"
        >
          <SvgIcon style="width: 16px; height: 16px" class="text-secondary-color" :name="`align-${align}`"> </SvgIcon>
        </button>
      </div>
    </BubbleMenu>

    <EditorContent :editor="editor" />
    <LinkModal
      v-if="modalLinkIsOpen"
      v-model="url"
      @removeLink="removeLink"
      @saveLink="saveLink"
      @closeModal="closeModal"
    ></LinkModal>
  </div>
</template>

<style scoped lang="scss">
.container-bubble {
  @apply flex rounded-lg items-center justify-center gap-1 p-[4px];
  background-color: var(--background-grey-1);
}

.bubble-button {
  @apply flex rounded-lg items-center justify-center border w-[36px];
  border-color: var(--grey-medium-light);
  background: var(--grey-light);

  &:hover,
  &:hover * {
    border-color: var(--primary-blue-color);
    color: var(--primary-blue-color);
  }

  &:active,
  &:active * {
    border-color: var(--blue-deep-dark);
    color: var(--blue-deep-dark);
  }
}

.editor-container {
  @apply flex flex-col gap-[8px] my-2;
}

.control-group {
  @apply flex flex-row items-center gap-[28px] flex-nowrap;
}

:deep(.ProseMirror) {
  a,
  a * {
    color: var(--primary-blue-color) !important;
    cursor: pointer;
  }

  a:hover,
  a:hover * {
    text-decoration: underline;
  }
}

.color-group {
  :deep(.panelButtonActions:not(:first-child)) {
    margin-left: -1px;
  }

  :deep(.panelButtonActions:hover),
  :deep(.panelButtonActions:has(.default-active)) {
    z-index: 1;
  }
}
</style>
